/**
 * Ba mức bắt buộc xác thực hai lớp (ADR-0015) — luật thuần, test bằng `node:test`.
 *
 * Backend chỉ có hai nguồn: cờ `mfa_required` của chính sách tổ chức và tập người
 * có cờ riêng (`/users/mfa-required`). "Mức" là thứ giao diện **suy ra** từ hai
 * nguồn đó, không phải một trường được lưu:
 *
 * | Chính sách tổ chức | Tập người có cờ riêng | Mức              |
 * | ------------------ | --------------------- | ---------------- |
 * | `true`             | bất kỳ                | `ca-to-chuc`     |
 * | `false`            | ≥ 1 người             | `nguoi-duoc-chon`|
 * | `false`            | rỗng                  | `khong`          |
 *
 * Ở mức `ca-to-chuc`, tập cờ riêng vẫn được giữ nguyên (không gửi `PUT` danh
 * sách): thu hẹp về "người được chọn" sau đó thì lựa chọn cũ hiện lại.
 */

export type MucMfa = 'ca-to-chuc' | 'nguoi-duoc-chon' | 'khong';

export function suyMuc(toChucBatBuoc: boolean, nguoiDuocChon: readonly string[]): MucMfa {
  if (toChucBatBuoc) return 'ca-to-chuc';
  return nguoiDuocChon.length > 0 ? 'nguoi-duoc-chon' : 'khong';
}

/**
 * Hộp "Tất cả mọi người trong tổ chức" đứng đầu danh sách chọn người.
 *
 * - `checked`: đang ở mức `ca-to-chuc`.
 * - `indeterminate`: chưa chọn "Tất cả" nhưng đã tích ít nhất một người — kể cả
 *   khi tích đủ từng người, vì như thế vẫn chưa phủ người được thêm sau.
 * - `duTungNguoi`: tích đủ mọi người đang có mà chưa chọn "Tất cả" — giao diện
 *   nhắc rằng người được thêm sau sẽ không bị buộc. `tatCa` là id mọi người
 *   trong tổ chức, `undefined` khi chưa tải được (khi đó không nhắc).
 */
export function trangThaiTatCa(
  caToChuc: boolean,
  daChon: ReadonlySet<string>,
  tatCa: readonly string[] | undefined,
): { checked: boolean; indeterminate: boolean; duTungNguoi: boolean } {
  if (caToChuc) return { checked: true, indeterminate: false, duTungNguoi: false };
  return {
    checked: false,
    indeterminate: daChon.size > 0,
    duTungNguoi: Boolean(tatCa && tatCa.length > 0 && tatCa.every((id) => daChon.has(id))),
  };
}

export interface ChenhLech {
  /** Có trong tập sau, không có trong tập trước — giữ thứ tự của tập sau. */
  them: string[];
  /** Có trong tập trước, không còn trong tập sau — giữ thứ tự của tập trước. */
  bo: string[];
}

export function chenhLech(truoc: readonly string[], sau: readonly string[]): ChenhLech {
  const a = new Set(truoc);
  const b = new Set(sau);
  return {
    them: [...b].filter((id) => !a.has(id)),
    bo: [...a].filter((id) => !b.has(id)),
  };
}

/** Hai tập bằng nhau, không kể thứ tự và phần tử lặp. */
export function cungTap(a: readonly string[], b: readonly string[]): boolean {
  const { them, bo } = chenhLech(a, b);
  return them.length === 0 && bo.length === 0;
}

/** Bật hoặc bỏ một id trong tập chọn, trả tập mới (không sửa tập cũ). */
export function batTatId(tap: ReadonlySet<string>, id: string): Set<string> {
  const moi = new Set(tap);
  if (moi.has(id)) moi.delete(id);
  else moi.add(id);
  return moi;
}

/**
 * Hai lời gọi cần gửi để đi từ trạng thái đã lưu sang lựa chọn mới.
 *
 * - `danhSach`: tập gửi `PUT /users/mfa-required`, `null` là không gửi. Mức
 *   `ca-to-chuc` không đụng tập cờ riêng; về `khong` thì xoá sạch tập.
 * - `toChuc`: giá trị `mfa_required` của chính sách tổ chức sau khi lưu.
 *
 * Phía gọi gửi danh sách **trước** chính sách: thu hẹp từ "cả tổ chức" về
 * "người được chọn" mà làm ngược lại thì có một khoảng người được chọn cũng
 * không bị buộc OTP.
 */
export function keHoachLuu(
  muc: MucMfa,
  daChon: readonly string[],
  daLuu: readonly string[],
): { danhSach: string[] | null; toChuc: boolean } {
  if (muc === 'ca-to-chuc') return { danhSach: null, toChuc: true };
  const sau = muc === 'khong' ? [] : [...new Set(daChon)];
  return { danhSach: cungTap(sau, daLuu) ? null : sau, toChuc: false };
}

export interface TrangThaiMfa {
  muc: MucMfa;
  /** Tập người có cờ riêng — chỉ có nghĩa ở mức `nguoi-duoc-chon`. */
  daChon: readonly string[];
}

/**
 * Một dòng hậu quả của việc lưu, để hộp xác nhận và câu mô tả nói đúng ai bị
 * ảnh hưởng. View đổi mỗi loại thành một câu; `so` là `null` khi chưa tải
 * được danh sách người dùng nên không đếm được.
 *
 * | Từ → sang                 | Dòng                                  |
 * | ------------------------- | ------------------------------------- |
 * | không → cả tổ chức        | `batCaToChuc`                         |
 * | người được chọn → cả      | `moRongCaToChuc` (số người mới bị buộc)|
 * | cả → người được chọn      | `thuHep` (số người giữ, số người thôi) |
 * | cả → không                | `tatCaToChuc`                         |
 * | không/người được chọn → người được chọn | `themNguoi`, `boNguoi`  |
 * | người được chọn → không   | `boNguoi`                             |
 */
export type HauQuaMfa =
  | { loai: 'batCaToChuc' }
  | { loai: 'moRongCaToChuc'; so: number | null }
  | { loai: 'thuHep'; giu: number; thoi: number | null }
  | { loai: 'tatCaToChuc' }
  | { loai: 'themNguoi'; so: number }
  | { loai: 'boNguoi'; so: number };

/**
 * Hậu quả khi đi từ `truoc` sang `sau`. `tatCa` là id mọi người trong tổ chức,
 * `undefined` khi chưa tải được. Không đổi gì thì trả mảng rỗng.
 */
export function hauQuaMfa(
  truoc: TrangThaiMfa,
  sau: TrangThaiMfa,
  tatCa: readonly string[] | undefined,
): HauQuaMfa[] {
  const daChonTruoc = truoc.muc === 'nguoi-duoc-chon' ? truoc.daChon : [];
  const daChonSau = sau.muc === 'nguoi-duoc-chon' ? sau.daChon : [];

  if (sau.muc === 'ca-to-chuc') {
    if (truoc.muc === 'ca-to-chuc') return [];
    if (truoc.muc === 'khong') return [{ loai: 'batCaToChuc' }];
    return [{ loai: 'moRongCaToChuc', so: tatCa ? chenhLech(daChonTruoc, tatCa).them.length : null }];
  }

  if (truoc.muc === 'ca-to-chuc') {
    if (sau.muc === 'khong') return [{ loai: 'tatCaToChuc' }];
    const giu = new Set(daChonSau);
    return [
      {
        loai: 'thuHep',
        giu: giu.size,
        thoi: tatCa ? tatCa.filter((id) => !giu.has(id)).length : null,
      },
    ];
  }

  const { them, bo } = chenhLech(daChonTruoc, daChonSau);
  const ra: HauQuaMfa[] = [];
  if (them.length > 0) ra.push({ loai: 'themNguoi', so: them.length });
  if (bo.length > 0) ra.push({ loai: 'boNguoi', so: bo.length });
  return ra;
}

/**
 * Lần lưu có làm thêm ai **mới** bị buộc OTP không — chỉ khi đó lựa chọn "đăng
 * xuất ngay" (`apply_now`) mới có nghĩa. Thu hẹp hay bỏ bắt buộc thì không ai
 * bị đăng xuất.
 */
export function coNguoiMoiBiBuoc(truoc: TrangThaiMfa, sau: TrangThaiMfa): boolean {
  return hauQuaMfa(truoc, sau, undefined).some(
    (h) => h.loai === 'batCaToChuc' || h.loai === 'moRongCaToChuc' || h.loai === 'themNguoi',
  );
}

/** Bỏ dấu tiếng Việt và viết thường, để "binh" tìm ra "Bình". */
function boDau(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
}

/** Lọc người theo tên hoặc email, không phân biệt dấu và hoa thường. */
export function locNguoi<T extends { display_name: string; email: string | null }>(
  ds: readonly T[],
  tuKhoa: string,
): T[] {
  const k = boDau(tuKhoa.trim());
  if (!k) return [...ds];
  return ds.filter((u) => boDau(u.display_name).includes(k) || boDau(u.email ?? '').includes(k));
}

/**
 * Dòng `auth.mfa_required` của chính sách tầng tenant (cấu hình ba tầng,
 * ADR-0059) — đủ để biết Tenant Admin chọn được mức nào.
 */
export interface DongMfaToChuc {
  value: unknown;
  inherited_value: unknown;
  editable: boolean;
  /** Khoá đang ép bật (ADR-0099: tổ chức cho phép email ngoài domain đã xác minh). */
  forced_by?: string | null;
}

/**
 * Mức cả tổ chức bị tầng trên **ép**: đang bắt buộc mà Tenant Admin không gỡ
 * được — tầng nền tảng hay gói đã bật (Tenant Admin chỉ được siết), hoặc khoá
 * không sửa được ở tầng tenant. Khi đó chỉ còn mức `ca-to-chuc`.
 */
export function epCaToChuc(dong: DongMfaToChuc): boolean {
  return dong.value === true && (!dong.editable || dong.inherited_value === true);
}

/**
 * Không chọn được mức `ca-to-chuc`: khoá `auth.mfa_required` không sửa được ở
 * tầng tenant (Tenant Admin vẫn chọn được người riêng).
 */
export function khoaCaToChuc(dong: DongMfaToChuc): boolean {
  return !dong.editable;
}
